import React from "react";

// ================= 用户头像组件开始 =================
// 默认使用 boring-avatars 的 beam 主题生成 SVG 头像
// props:
//   name: 用作随机种子，将生成稳定头像
//   size: 像素大小，默认 40
//   square: 是否方形，默认 true（符合设计语言）
//   src: 若传入则优先显示该图片
// =====================================================

type UserAvatarProps = {
  /** 生成占位头像的种子，一般传用户昵称 */
  name: string;
  /** 远端头像 URL，若提供则优先展示 */
  src?: string | null;
  /** 尺寸，像素值。若不传则可通过 Tailwind class 控制 w/h */
  size?: number;
  /** 是否方形，Boring Avatars 默认为方形，若 square=false 则渲染圆形 */
  square?: boolean;
  /** 额外 Tailwind className */
  className?: string;
  /** img alt 文案 */
  alt?: string;
};

export default function UserAvatar({
  name,
  src,
  size,
  square = true,
  className = "",
  alt,
}: UserAvatarProps) {
  const [hasError, setHasError] = React.useState(false);

  // 当 src 改变时，重置错误状态
  React.useEffect(() => {
    setHasError(false);
  }, [src]);

  const dimensionStyle =
    typeof size === "number" ? { width: size, height: size } : undefined;

  // 检查 className 中是否已包含 rounded 样式
  const hasCustomRounded = /rounded-/.test(className);
  const containerRounded = hasCustomRounded ? "" : "rounded-2xl";
  const imgRounded = hasCustomRounded
    ? ""
    : square
      ? "rounded-2xl"
      : "rounded-full";

  // 使用传入的 src，除非加载失败
  const shouldShowSrc = src && !hasError;

  return (
    <div
      className={`inline-block overflow-hidden ${containerRounded} ${className}`}
      style={dimensionStyle}
    >
      {shouldShowSrc ? (
        <img
          src={src!}
          alt={alt || name}
          className={`w-full h-full object-cover ${imgRounded}`}
          onError={() => setHasError(true)}
        />
      ) : (
        <img
          src={`https://api.dicebear.com/9.x/initials/svg?seed=${encodeURIComponent(
            name
          )}`}
          alt={alt || name}
          className={`w-full h-full object-cover ${imgRounded}`}
        />
      )}
    </div>
  );
}

// ================= 用户头像组件结束 =================
